import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const PHONE_SETUP_PATH = '/phone-setup';
const ROLE_SELECTION_PATH = '/role-selection';

const ProtectedRoute = ({ children, requiredRole = null }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Phone not verified — must complete phone setup first
    if (!user?.phone_verified && location.pathname !== PHONE_SETUP_PATH) {
        return <Navigate to={PHONE_SETUP_PATH} replace />;
    }

    // Phone verified but no role yet — go to role selection
    if (user?.phone_verified && !user?.role && location.pathname !== ROLE_SELECTION_PATH) {
        return <Navigate to={ROLE_SELECTION_PATH} replace />;
    }

    // Phone verified + role set → prevent accessing setup pages again
    if (user?.phone_verified && user?.role) {
        if (location.pathname === PHONE_SETUP_PATH || location.pathname === ROLE_SELECTION_PATH) {
            const dashboardPath = user.role === 'STUDENT' ? '/student/dashboard' : '/employer/dashboard';
            return <Navigate to={dashboardPath} replace />;
        }
    }

    // Role mismatch — redirect to their actual dashboard
    if (requiredRole && user?.role !== requiredRole) {
        const dashboardPath = user.role === 'STUDENT' ? '/student/dashboard' : '/employer/dashboard';
        return <Navigate to={dashboardPath} replace />;
    }

    return children;
};

export default ProtectedRoute;
